import { useContext } from 'react'

import { MODALS_ID } from '../../../constants'
import type { components } from '../../../constants/schema'

import { calculateAge } from '../../../utils'

import { getHoldingProductList } from '../../../api/product'

import { ModalContext } from '../../../provider/ModalProvider'

import { useAlert } from '../../../hooks'
import { useNavigate } from '../../../hooks/useNavigate'
import { useUserInfo } from '../../../hooks/useUserInfo'
import useParamId from '../../../hooks/useParamId'

import { Button } from '../../../components/button'
import { Text } from '../../../components/text'
import type { ButtonProps } from '../../../components/button/Button'

import { Spinner } from '../../../components/spinner/Spinner'
import { AddressModal } from '../modals/AddressModal'
import { SuitableNoticeModal } from '../modals/SuitableNoticeModal'
import { VerificationNoticeModal } from '../modals/VerificationNoticeModal'
import { CreateAccountModal } from '../../settings/modal'

interface FooterButtonProps {
  subscriptionStatus?: components['schemas']['ProductDetailDto']['subscriptionStatus']
  subscriptionStatusLabel?: components['schemas']['ProductDetailDto']['subscriptionStatusLabel']
}

export function FooterButton({ subscriptionStatus, subscriptionStatusLabel }: FooterButtonProps) {
  const { openModal } = useContext(ModalContext)

  const navigate = useNavigate()
  const productId = useParamId()
  const openAlert = useAlert()

  const { data: userInfo } = useUserInfo()

  const goMyAssetProduct = async () => {
    const response = await getHoldingProductList({ size: 100, offset: 0 })

    const assetId = response.list.find((asset) => asset.productId === productId)?.id
    if (typeof assetId === 'undefined') return

    navigate(`/myPage/asset/${assetId}?redirectUrl=${window.location.pathname}`)
  }

  const order = () => {
    if (typeof userInfo?.birthday === 'undefined' || calculateAge(userInfo?.birthday) < 19) {
      openAlert({
        content: (
          <Text size={16} weight={500} lineHeight={24} $textAlign="center">
            만19세 이상부터 구매가 가능해요.
            <br />
            자세한 내용은 FAQ를 참고해주세요.
            <br />
          </Text>
        ),
      })
      return
    }

    navigate(`/market/product/${productId}/order`)
  }

  const buttonProps: () => ButtonProps = () => {
    switch (subscriptionStatus) {
      case 'TO_BE_RELEASED': // 청약 예정
      case 'SUBSCRIPTION_CLOSED': // 청약 종료됨
      case 'UNKNOWN': // 오류
        return { disabled: true }

      // 로그인 후 이용이 가능해요
      case 'NEED_LOGIN':
        return { type: 'dark', href: '/signIn' }

      // 본인인증이 필요합니다
      case 'NEED_IDENTIFY':
        return {
          type: 'dark',
          onClick: () => openModal({ id: MODALS_ID.VERIFICATION_NOTICE, Component: VerificationNoticeModal }),
        }

      // 주소입력이 필요합니다
      case 'NEED_ADDRESS':
        return { type: 'dark', onClick: () => openModal({ id: MODALS_ID.EMPTY_ADDRESS, Component: AddressModal }) }

      // 입출금계좌를 개설해주세요
      case 'NEED_DEPOSIT_ACCOUNT':
        return {
          type: 'dark',
          onClick: () => openModal({ id: MODALS_ID.CREATE_ACCOUNT, Component: CreateAccountModal }),
        }

      // 투자적합성 테스트를 완료하세요
      case 'NEED_SUITABILITY_TEST':
        return {
          type: 'dark',
          onClick: () => openModal({ id: MODALS_ID.SUITABLE_NOTICE, Component: SuitableNoticeModal }),
        }

      // 청약 신청하기
      case 'SUBSCRIPTION_AVAILABLE':
        return { type: 'primary', onClick: order }

      // 청약 신청 내역 확인하기
      case 'HAS_SUBSCRIPTION':
        return { type: 'primary', onClick: goMyAssetProduct }

      default:
        return { disabled: true }
    }
  }

  return (
    <Button {...buttonProps()} size="large" block={true}>
      {subscriptionStatusLabel ?? <Spinner />}
    </Button>
  )
}

export type { FooterButtonProps }
